export type JpegSegment = {
  readonly marker: number;
  /** Offset of the first payload byte (after the two length bytes). */
  readonly dataOffset: number;
  /** Payload length, excluding the two length bytes. */
  readonly dataLength: number;
};

/**
 * Why the walk stopped: at the scan data (every header segment was seen), because
 * the buffer ended first (read more and retry), or because the bytes are not a JPEG
 * header at all.
 */
export type JpegWalkStatus = 'complete' | 'truncated' | 'corrupt';

const STANDALONE_MARKERS = new Set([0x01, 0xd0, 0xd1, 0xd2, 0xd3, 0xd4, 0xd5, 0xd6, 0xd7, 0xd8]);
const SCAN_MARKER = 0xda;
const END_MARKER = 0xd9;

/**
 * Yields the header segments of a JPEG in order until the scan data starts.
 * Only segments whose payload is fully inside `bytes` are yielded; the return
 * value says whether the header was read completely.
 */
export function* walkJpegSegments(bytes: Uint8Array): Generator<JpegSegment, JpegWalkStatus> {
  let offset = 2;
  while (true) {
    if (offset + 2 > bytes.length) {
      return 'truncated';
    }
    if (bytes[offset] !== 0xff) {
      return 'corrupt';
    }
    const marker = bytes[offset + 1];
    if (marker === undefined) {
      return 'truncated';
    }
    if (marker === 0xff) {
      offset += 1;
      continue;
    }
    if (marker === SCAN_MARKER || marker === END_MARKER) {
      return 'complete';
    }
    if (STANDALONE_MARKERS.has(marker)) {
      offset += 2;
      continue;
    }
    const high = bytes[offset + 2];
    const low = bytes[offset + 3];
    if (high === undefined || low === undefined) {
      return 'truncated';
    }
    const segmentLength = (high << 8) | low;
    if (segmentLength < 2) {
      return 'corrupt';
    }
    if (offset + 2 + segmentLength > bytes.length) {
      return 'truncated';
    }
    yield {marker, dataOffset: offset + 4, dataLength: segmentLength - 2};
    offset += 2 + segmentLength;
  }
}
